import React from 'react';
import { RadioProps, Radio as TUIRadio, Label, Box } from '@apia/theme';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { FieldWrapper } from './FieldWrapper';
import { TDefaultFieldType } from './types';

export type TRadio = RadioProps &
  TDefaultFieldType & {
    options: { value: string; label: string }[];
  };

export const Radio = React.forwardRef<HTMLInputElement, TRadio>(
  (
    {
      avoidSemicolon,
      className,
      formName,
      label,
      onChange: outerOnChange,
      options,
      value: outerValue,
      /**
       * useField
       */
      avoidRegisterIfExists,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });
    const { value, updateValidations, onChange, isTouched, ...inputProps } =
      useField<string>({
        formName,
        name,
        initialValue: initialValue ?? (outerValue as string),
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });
    const handleOnChange = React.useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        onChange(ev);
        if (outerOnChange) {
          outerOnChange(ev);
        }
      },
      [onChange, outerOnChange],
    );
    return (
      <FieldWrapper
        name={name}
        label={label}
        className={`radio ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
        as="ul"
        title={props.title}
      >
        {options?.map((option) => (
          <li key={option.value}>
            <Label title={option.label} className="radio-label">
              <TUIRadio
                {...props}
                {...inputProps}
                role="radio"
                className="radio-button"
                aria-label={option.label}
                ref={ref}
                value={option.value}
                checked={value === option.value}
                onChange={handleOnChange}
              />
              <Box as="span" className="radio-text">
                {option.label}
              </Box>
            </Label>
          </li>
        ))}
      </FieldWrapper>
    );
  },
);

Radio.displayName = 'Radio';
